import { type ReactNode, useCallback } from 'react';
import { Navigate } from 'react-router-dom';

import { useUser } from '@/allauth_auth/hooks.js';
import { usePortfolios } from '@/hooks/usePortfolios';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { QueryErrorState } from '@/lib/components/QueryErrorState';

interface PortfolioGuardProps {
  children: ReactNode;
}

/**
 * Guards routes to only allow users with portfolio access.
 *
 * Consumes usePortfolios() directly (rather than the boolean
 * usePortfolioAccess() gate) so a transport/server failure (5xx, network)
 * can be told apart from a confirmed "no access" response: an outage shows
 * a retryable error, it does not redirect away from the Portfolio area.
 */
export function PortfolioGuard({ children }: PortfolioGuardProps) {
  const user = useUser();
  const { data: portfolios, isError, error, refetch } = usePortfolios();

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Still resolving the user, or the query hasn't settled yet.
  if (!user?.id || (portfolios === undefined && !isError)) {
    return (
      <div className='flex items-center justify-center min-h-dvh'>
        <LoadingIndicator size='md' />
      </div>
    );
  }

  // Transport/server failure — surface a retryable error, don't redirect.
  if (isError) {
    return (
      <div className='flex items-center justify-center min-h-dvh'>
        <QueryErrorState
          error={error}
          label="We couldn't load your portfolio access"
          onRetry={handleRetry}
        />
      </div>
    );
  }

  // Resolved without any portfolios — confirmed no access, redirect.
  if (portfolios.length === 0) {
    return <Navigate to='/organizations' replace />;
  }

  return children;
}
